import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import './CsvImportDialog.scss';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface CsvFieldDescriptor {
  /** camelCase property name on the entity */
  key: string;
  /** Human-readable label shown in the mapping table */
  label: string;
  /** When true the field must be mapped before the import can run */
  required?: boolean;
}

export interface CsvImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

export interface CsvImportDialogProps {
  open: boolean;
  /** Dialog title, e.g. "Import Supplier Invoices from CSV" */
  title: string;
  /** Describes every importable field on the target entity (exclude id) */
  entityFields: CsvFieldDescriptor[];
  /**
   * Called when the user clicks Import.
   * Return a promise resolving to import statistics.
   */
  onImport: (
    file: File,
    mapping: Record<string, string>,
    delimiter: string,
  ) => Promise<CsvImportResult>;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type Step = 'upload' | 'mapping' | 'result';

/** Parses the first line of a CSV string and returns the column headers. */
function parseCsvHeaders(content: string, delimiter: string): string[] {
  const firstLine = content.split(/\r?\n/)[0] ?? '';
  const headers: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < firstLine.length; i++) {
    const c = firstLine[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === delimiter && !inQuotes) {
      headers.push(current.trim());
      current = '';
    } else {
      current += c;
    }
  }
  headers.push(current.trim());
  return headers.filter((h) => h.length > 0);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CsvImportDialog: React.FC<CsvImportDialogProps> = ({
  open,
  title,
  entityFields,
  onImport,
  onClose,
}) => {
  const { t } = useTranslation(['common', 'screens']);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [delimiter, setDelimiter] = useState(',');
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- reset -----------------------------------------------------------------

  const reset = () => {
    setStep('upload');
    setFile(null);
    setCsvHeaders([]);
    setMapping({});
    setDelimiter(',');
    setResult(null);
    setError(null);
    setImporting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // ---- file handling ---------------------------------------------------------

  const loadFileHeaders = (f: File, delim: string) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = (e.target?.result as string) ?? '';
      const headers = parseCsvHeaders(content, delim);
      setCsvHeaders(headers);
      setMapping({});
      setStep('mapping');
    };
    reader.readAsText(f);
  };

  const handleFileChange = (f: File | null) => {
    if (!f) return;
    setFile(f);
    loadFileHeaders(f, delimiter);
  };

  const handleDelimiterChange = (newDelim: string) => {
    setDelimiter(newDelim);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = (e.target?.result as string) ?? '';
        setCsvHeaders(parseCsvHeaders(content, newDelim));
        setMapping({});
      };
      reader.readAsText(file);
    }
  };

  // ---- mapping ---------------------------------------------------------------

  const handleMappingChange = (entityKey: string, csvColumn: string) => {
    setMapping((prev) => ({ ...prev, [entityKey]: csvColumn }));
  };

  // ---- import ----------------------------------------------------------------

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const res = await onImport(file, mapping, delimiter);
      setResult(res);
      setStep('result');
    } catch (e: any) {
      setError(e.message ?? t('common:error'));
    } finally {
      setImporting(false);
    }
  };

  // ---- render steps ----------------------------------------------------------

  const renderUploadStep = () => (
    <div className="csv-import-upload">
      <div className="csv-import-delimiter-row">
        <label className="form-label mb-0">{t('screens:csvImport.delimiter')}</label>
        <select
          className="form-select form-select-sm"
          value={delimiter}
          onChange={(e) => handleDelimiterChange(e.target.value)}
        >
          <option value=",">{t('screens:csvImport.delimiterComma')}</option>
          <option value=";">{t('screens:csvImport.delimiterSemicolon')}</option>
          <option value={'\t'}>{t('screens:csvImport.delimiterTab')}</option>
        </select>
      </div>

      <div
        className="csv-import-dropzone"
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFileChange(e.dataTransfer.files[0] ?? null);
        }}
      >
        <span className="csv-import-dropzone-icon" aria-hidden="true">📂</span>
        <p>{t('screens:csvImport.dropzoneText')}</p>
        <button type="button" className="btn btn-outline-primary btn-sm">
          {t('screens:csvImport.browseFiles')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          style={{ display: 'none' }}
          onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
        />
      </div>

      {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}
    </div>
  );

  const renderMappingStep = () => (
    <div className="csv-import-mapping">
      <p className="csv-import-file-info">
        <strong>{t('screens:csvImport.selectedFile')}:</strong> {file?.name}
        &nbsp;&bull;&nbsp;
        <strong>{t('screens:csvImport.csvColumns')}:</strong> {csvHeaders.length}
      </p>

      <div className="csv-import-mapping-scroll">
        <table className="csv-import-mapping-table table table-sm table-hover mb-0">
          <thead>
            <tr>
              <th>{t('screens:csvImport.entityField')}</th>
              <th>{t('screens:csvImport.csvColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {entityFields.map((field) => (
              <tr key={field.key}>
                <td>
                  {field.label}
                  {field.required && <span className="text-danger ms-1">*</span>}
                </td>
                <td>
                  <select
                    className="form-select form-select-sm"
                    value={mapping[field.key] ?? ''}
                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                  >
                    <option value="">{t('common:select')}</option>
                    {csvHeaders.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}
    </div>
  );

  const renderResultStep = () => (
    <div className="csv-import-result">
      <div className="csv-import-result-stats">
        <div className="csv-import-stat csv-import-stat--success">
          <span className="csv-import-stat-value">{result?.imported ?? 0}</span>
          <span className="csv-import-stat-label">{t('screens:csvImport.imported')}</span>
        </div>
        <div className="csv-import-stat csv-import-stat--warning">
          <span className="csv-import-stat-value">{result?.skipped ?? 0}</span>
          <span className="csv-import-stat-label">{t('screens:csvImport.skipped')}</span>
        </div>
      </div>

      {result?.errors && result.errors.length > 0 && (
        <div className="csv-import-errors">
          <h6>{t('screens:csvImport.errors')}</h6>
          <ul className="csv-import-error-list">
            {result.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // ---- action bar ------------------------------------------------------------

  const renderActions = () => {
    if (step === 'upload') {
      return (
        <button type="button" className="btn btn-secondary" onClick={handleClose}>
          {t('common:cancel')}
        </button>
      );
    }

    if (step === 'mapping') {
      return (
        <>
          <button type="button" className="btn btn-secondary" onClick={() => setStep('upload')}>
            {t('common:back')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={importing}
            onClick={handleImport}
          >
            {importing ? t('screens:csvImport.importing') : t('screens:csvImport.import')}
          </button>
        </>
      );
    }

    // result step
    return (
      <>
        <button type="button" className="btn btn-secondary" onClick={reset}>
          {t('screens:csvImport.importAnother')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleClose}>
          {t('common:close')}
        </button>
      </>
    );
  };

  // ---- portal ----------------------------------------------------------------

  if (!open) return null;

  const dialog = (
    <div className="modal-backdrop">
      <div className="modal-confirm csv-import-popup">
        {/* close button */}
        <button
          type="button"
          className="modal-close-btn"
          aria-label="Close"
          onClick={handleClose}
        >
          &times;
        </button>

        <h5 className="csv-import-title">{title}</h5>

        {/* step indicators */}
        <div className="csv-import-steps">
          <div className={`csv-import-step${step === 'upload' ? ' active' : ' done'}`}>
            1. {t('screens:csvImport.stepUpload')}
          </div>
          <div className={`csv-import-step${step === 'mapping' ? ' active' : step === 'result' ? ' done' : ''}`}>
            2. {t('screens:csvImport.stepMap')}
          </div>
          <div className={`csv-import-step${step === 'result' ? ' active' : ''}`}>
            3. {t('screens:csvImport.stepResult')}
          </div>
        </div>

        {/* step content */}
        <div className="csv-import-step-content">
          {step === 'upload'  && renderUploadStep()}
          {step === 'mapping' && renderMappingStep()}
          {step === 'result'  && renderResultStep()}
        </div>

        {/* action bar */}
        <div className="modal-actions csv-import-actions">
          {renderActions()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default CsvImportDialog;

